import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Mail, Send, Eye, Loader2 } from "lucide-react";
import { API_BASE } from "@/lib/config";

/**
 * Admin-only email tester.
 *
 * Lists every transactional template the backend knows about, lets the
 * admin tweak the params (name, days_left, etc.), preview the rendered
 * HTML in a sandboxed iframe, and fire a real Resend email to any address.
 */
export default function EmailTester({ adminEmail }) {
  const [templates, setTemplates] = useState([]);
  const [resendOk, setResendOk] = useState(false);
  const [sender, setSender] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [params, setParams] = useState({});
  const [preview, setPreview] = useState(null); // {subject, html, text}
  const [previewing, setPreviewing] = useState(false);
  const [previewErr, setPreviewErr] = useState("");

  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState(null); // {type:"ok"|"err", text}

  const selected = useMemo(
    () => templates.find((t) => t.id === selectedId),
    [templates, selectedId],
  );

  // Load templates on mount.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/admin/email-templates`);
        const list = data.templates || [];
        setTemplates(list);
        setResendOk(!!data.resend_configured);
        setSender(data.sender || "");
        if (data.admin_email) setTo((cur) => cur || data.admin_email);
        // Pick the first template + seed its default params in one shot so
        // we don't need a follow-up effect (which trips set-state-in-effect).
        if (list.length) {
          setSelectedId(list[0].id);
          const defaults = {};
          (list[0].params || []).forEach((p) => { defaults[p.key] = p.default; });
          setParams(defaults);
        }
      } catch (e) {
        setPreviewErr(e?.response?.data?.detail || e?.message || "Failed to load templates");
      }
    })();
  }, []);

  // Reset params + preview when switching templates.
  function pickTemplate(id) {
    setSelectedId(id);
    const tpl = templates.find((t) => t.id === id);
    const next = {};
    (tpl?.params || []).forEach((p) => { next[p.key] = p.default; });
    setParams(next);
    setPreview(null);
    setSendMsg(null);
    setPreviewErr("");
  }

  async function doPreview() {
    if (!selected) return;
    setPreviewing(true);
    setPreviewErr("");
    setSendMsg(null);
    try {
      const { data } = await axios.post(`${API_BASE}/admin/email-preview`, {
        template: selected.id,
        params,
      });
      setPreview(data);
    } catch (e) {
      setPreviewErr(e?.response?.data?.detail || e?.message || "Preview failed");
    } finally { setPreviewing(false); }
  }

  async function doSend() {
    if (!selected || !to) return;
    setSending(true);
    setSendMsg(null);
    try {
      const { data } = await axios.post(`${API_BASE}/admin/email-send`, {
        template: selected.id,
        params,
        to,
      });
      setSendMsg({ type: "ok", text: `Sent to ${data.to} · id ${data.id}` });
    } catch (e) {
      setSendMsg({ type: "err", text: e?.response?.data?.detail || e?.message || "Send failed" });
    } finally { setSending(false); }
  }

  function setParam(key, value) {
    setParams((p) => ({ ...p, [key]: value }));
  }

  return (
    <section
      className="bg-white border border-[var(--app-border)] rounded-md overflow-hidden"
      data-testid="admin-email-tester"
    >
      <div className="px-5 py-3 border-b border-[var(--app-border)] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Mail className="w-3.5 h-3.5 text-[var(--app-text-soft)]" />
          <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)]">
            Transactional email tester
          </div>
        </div>
        <div className="text-[11px] font-mono-tech text-[var(--app-text-soft)] flex items-center gap-2">
          {resendOk
            ? <span data-testid="email-tester-resend-ok"><span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1 align-middle" />Resend ready · from {sender}</span>
            : <span className="text-amber-700" data-testid="email-tester-resend-missing">RESEND_API_KEY not set — preview only</span>}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-[var(--app-border)]">
        {/* Left: controls */}
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mb-1.5">
              Template
            </label>
            <select
              value={selectedId}
              onChange={(e) => pickTemplate(e.target.value)}
              data-testid="email-tester-template-select"
              className="w-full border border-[var(--app-border)] rounded px-2.5 py-1.5 text-sm bg-white"
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
            {selected?.description && (
              <p className="mt-1.5 text-xs text-[var(--app-text-soft)]">{selected.description}</p>
            )}
          </div>

          {(selected?.params || []).map((p) => (
            <div key={p.key} data-testid={`email-tester-param-${p.key}`}>
              <label className="block text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mb-1.5">
                {p.label}
              </label>
              {p.type === "boolean" ? (
                <label className="inline-flex items-center gap-2 text-sm select-none cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!params[p.key]}
                    onChange={(e) => setParam(p.key, e.target.checked)}
                    className="w-4 h-4 accent-blue-600"
                  />
                  <span className="text-[var(--app-text-soft)]">{p.label}</span>
                </label>
              ) : (
                <input
                  type={p.type === "number" ? "number" : "text"}
                  value={params[p.key] ?? ""}
                  onChange={(e) => setParam(p.key, p.type === "number" ? Number(e.target.value) : e.target.value)}
                  className="w-full border border-[var(--app-border)] rounded px-2.5 py-1.5 text-sm"
                />
              )}
            </div>
          ))}

          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mb-1.5">
              Send to (any address)
            </label>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder={adminEmail || "you@example.com"}
              data-testid="email-tester-to-input"
              className="w-full border border-[var(--app-border)] rounded px-2.5 py-1.5 text-sm font-mono-tech"
            />
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={doPreview}
              disabled={previewing || !selected}
              data-testid="email-tester-preview-btn"
              className="inline-flex items-center gap-1.5 text-sm font-display font-bold px-3 py-1.5 rounded border border-[var(--app-border)] bg-white hover:bg-[var(--app-bg)] disabled:opacity-50"
            >
              {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
              Preview
            </button>
            <button
              type="button"
              onClick={doSend}
              disabled={sending || !selected || !to || !resendOk}
              data-testid="email-tester-send-btn"
              className="inline-flex items-center gap-1.5 text-sm font-display font-bold px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              title={!resendOk ? "RESEND_API_KEY is not configured" : ""}
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Send test
            </button>
          </div>

          {previewErr && (
            <div className="text-xs text-red-600" data-testid="email-tester-error">{previewErr}</div>
          )}
          {sendMsg && (
            <div
              data-testid="email-tester-send-result"
              className={`text-xs font-mono-tech ${sendMsg.type === "ok" ? "text-emerald-700" : "text-red-600"}`}
            >
              {sendMsg.text}
            </div>
          )}
        </div>

        {/* Right: preview */}
        <div className="p-5 bg-[var(--app-bg)] min-h-[360px]">
          {!preview ? (
            <div className="h-full flex items-center justify-center text-xs text-[var(--app-text-soft)] italic" data-testid="email-tester-preview-empty">
              Click <span className="font-display font-bold mx-1">Preview</span> to render the email here.
            </div>
          ) : (
            <div className="space-y-2" data-testid="email-tester-preview-pane">
              <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)]">Subject</div>
              <div className="text-sm font-display font-bold" data-testid="email-tester-preview-subject">{preview.subject}</div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mt-3">Rendered HTML</div>
              <iframe
                title="email-preview"
                data-testid="email-tester-preview-iframe"
                sandbox=""
                srcDoc={preview.html}
                className="w-full bg-white border border-[var(--app-border)] rounded h-[480px]"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
